<script lang="ts">
export enum TabType {
  EXISTING = 'existing',
  NEW = 'new',
  SHARED = 'shared'
}
</script>

<script setup lang="ts">
import { ref, computed, onMounted, watch, Ref, PropType } from 'vue'
import { useRouter } from 'vue-router'
import GbButton from '@/components/GbButton.vue'
import GbDialog from '@/components/GbDialog.vue'
import FolderAvatar from '@/library/FolderAvatar.vue'
import { TooltipProvider, Tooltip, TooltipTrigger, TooltipContent } from '@/components/tooltip'
import { TabsRoot, TabsList, TabsTrigger, TabsContent, Label } from 'reka-ui'
import { useLibraryStore } from "@/library/library.store.js";
import { storeToRefs } from "pinia";
import { useUserStore } from "@/authentication/user.store.js";
import { LibraryFolder } from "@/library/library_types.ts";
import { postImpactOfDisabilityStatement } from "@/impact-of-disability/api.ts";
import GbInlineMessage from '@/components/GbInlineMessage.vue'
import { getAnalyticsTracker } from "@/tracking/analytics/plugin";
import InputText from 'primevue/inputtext'
import { useContentAccessStore } from '@/content_access/content_access.store'
import { CONTENT_TYPES } from '@/util/access_visibility'
import ProgressSpinner from 'primevue/progressspinner'
import NewFolderInput from '@/components/NewFolderInput.vue'
import { EVENT_NAMES } from '@/tracking/analytics/event_names.ts'
import { deprecatedPresentLevelsSaveEventData } from '@/library/library_event_names'
import { getFolderOwnerRole, getFolderOwnerUuid } from '@/library/library_helpers.ts'
import { useFolderContentQuota } from '@/library/composables/useFolderContentQuota'
import DOMPurify from 'dompurify'

const router = useRouter()

const contentAccessStore = useContentAccessStore()
const libraryStore = useLibraryStore()
const userStore = useUserStore()
const { folders: allFolders, currentFolder: libraryCurrentFolder, recentFolders, folderCount, loadingFolders, loadFoldersError, sharedFolders } = storeToRefs(libraryStore)

const { userId } = storeToRefs(userStore)
const analytics = getAnalyticsTracker()
const { maxFoldersReached, libraryQuotas } = storeToRefs(contentAccessStore)
const { folderQuotaErrorMessage } = useFolderContentQuota()

const emit = defineEmits<{
  save: [folder: { uuid: string, name: string }, savedContent: unknown]
  saveerror: [error: unknown]
  'update:visible': [value: boolean]
  'statement-created': [statement: any]
}>()

const props = defineProps({
  contentType: {
    type: String,
    required: true
  },
  contentTypeLabel: {
    type: String,
    required: true
  },
  contentUuid: {
    type: String,
    required: false,
    default: ''
  },
  customHeader: {
    type: String,
    required: false
  },
  preSaveFunction: {
    type: Function,
    required: false
  },
  postSaveNavigate: {
    type: Boolean,
    default: false
  },
  contentPayload: {
    type: Object,
    required: false
  },
  contentTitle: {
    type: String,
    required: false,
    default: ''
  },
  preselectedFolder: {
    type: Object as PropType<LibraryFolder>,
    required: false
  },
  enableNotes: {
    type: Boolean,
    default: false
  },
  notesPlaceholder: {
    type: String,
    default: ''
  },
  confirmSave: {
    type: Function as PropType<(folderName: string) => Promise<boolean>>,
    required: false
  },
  /** A folder never offered or preselected, such as the one a copy is being made from. */
  excludedFolderUuid: {
    type: String,
    default: ''
  }
})

const showDialog = ref(true)
// IDREFs are space-separated, so a multi-word label must not reach the id the dialog is labelled by.
const contentTypeSlug = computed(() => props.contentTypeLabel.toLowerCase().replace(/[^a-z0-9]+/g, '-'))
const dialogId = computed(() => {
  return `save-${contentTypeSlug.value}-modal-${props.contentUuid}`
})
const currentFolder: Ref<LibraryFolder | null> = ref(null)

const ownedFolderNameFilter = ref('')
const sharedFolderNameFilter = ref('')
const tabValue = ref(TabType.EXISTING)
const newFolderName = ref('')
const errorMessage = ref('')
const isSavingToFolder = ref(false)
const notes = ref('')
const analyticsContentTitle = computed(() => props.contentTitle || props.contentTypeLabel)
const contentProperties = computed(() => ({ cu: props.contentUuid, ct: analyticsContentTitle.value }))
const folderToPreselect = computed(() => props.preselectedFolder ?? libraryCurrentFolder.value)

function handleFolderQuotaExceeded() {
  analytics.sendEvent('Content Access Blocked', {
    blocked_action: 'Add Folder',
    blocking_quota: 'max_folders',
    blocking_count: folderCount.value,
    blocking_quota_value: libraryQuotas.value.maxFolders
  })
}

// The recent folders API returns folders without their roles, so ownership falls back to the folder's owner field.
const recentUserOwnedFolders = computed(() =>
  recentFolders.value.filter(folder => (getFolderOwnerUuid(folder) ?? folder.owner) === userId.value)
)

function isOffered(folder: LibraryFolder) {
  return folder.uuid !== props.excludedFolderUuid
}

const filteredRecentFolders = computed(() => {
  const offeredFolders = recentUserOwnedFolders.value.filter(isOffered)
  if (!ownedFolderNameFilter.value) return offeredFolders

  const filter = normalizeFolderName(ownedFolderNameFilter.value)
  return offeredFolders.filter(folder =>
    normalizeFolderName(folder.name).includes(filter)
  )
})

const filteredOtherFolders = computed(() => {
  const filter = normalizeFolderName(ownedFolderNameFilter.value)
  return libraryStore.userOwnedFolders.filter(isOffered).filter(folder => {
    const notInRecent = !recentUserOwnedFolders.value.some(recent =>
      recent.uuid === folder.uuid
    )
    if (!ownedFolderNameFilter.value) return notInRecent
    return notInRecent && normalizeFolderName(folder.name).includes(filter)
  })
})

const filteredSharedFolders = computed(() => {
  const filter = normalizeFolderName(sharedFolderNameFilter.value)
  return sharedFolders.value.filter(isOffered).filter(folder =>
    normalizeFolderName(folder.name).includes(filter)
  )
})

const isNewFolder = computed(() => {
  const newName = normalizeFolderName(newFolderName.value || '')
  if (!newName) return false
  return !allFolders.value.some(folder =>
    normalizeFolderName(folder.name) === newName
  )
})


const disableSaveButton = computed(() => {
  return !(currentFolder.value?.name || (tabValue.value === TabType.NEW && newFolderName.value && isNewFolder.value && !maxFoldersReached.value)) || isSavingToFolder.value
})

function isPresentlevel() {
  return props.contentType === CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY
}

function isImpactOfDisability() {
  return props.contentType === CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY
}

const saveButtonLabel = computed(() => {
  const action = tabValue.value === TabType.SHARED ? 'Contribute' : 'Save'
  const folderName = tabValue.value === TabType.NEW && newFolderName.value ? 'new folder' : 'folder'
  return `${action} to ${folderName}`
})

// Methods
function normalizeFolderName(name: string): string {
  return name.trim().toLowerCase()
}

function isSelectedFolder(folder: LibraryFolder) {
  return currentFolder.value?.uuid === folder.uuid
}

function selectFolder(folder: LibraryFolder) {
  currentFolder.value = folder
  newFolderName.value = ''
}

function newFolderNameUpdated() {
  if (newFolderName.value && currentFolder.value) {
    currentFolder.value = null
  }
}

function sendMaxFoldersReached() {
  analytics.sendEvent('Content Access Blocked', {
    blocked_action: 'Add Student',
    blocking_quota: 'max_students',
    blocking_count: folderCount.value,
    blocking_quota_value: libraryQuotas.value.maxFolders
  })
}

function handleTabValueChange(value: string) {
  tabValue.value = value as TabType
  if (value === TabType.NEW && maxFoldersReached.value) {
    sendMaxFoldersReached()
  }
  if (value === TabType.EXISTING) {
    newFolderName.value = ''
  } else {
    currentFolder.value = null
  }
}

function targetFolderName() {
  return tabValue.value === TabType.NEW ? newFolderName.value : currentFolder.value?.name ?? ''
}

// A contribution to a shared folder already carries its own edit-access warning in the
// Shared With Me tab, so it skips the extra confirmation.
async function isSaveDeclined() {
  const needsConfirmation = props.confirmSave && tabValue.value !== TabType.SHARED
  if (!needsConfirmation) return false
  return !(await props.confirmSave!(targetFolderName()))
}

async function saveToFolder() {
  if (await isSaveDeclined()) {
    showDialog.value = false
    return
  }
  isSavingToFolder.value = true
  errorMessage.value = ''
  try {
    const quotaError = existingFolderQuotaError()
    if (quotaError) {
      errorMessage.value = quotaError
      return
    }
    if (props.preSaveFunction) {
        await props.preSaveFunction()
    }
    // Read before the folder exists, after which the name is no longer new.
    const isSavingToNewFolder = tabValue.value === TabType.NEW && Boolean(newFolderName.value) && isNewFolder.value
    if (isSavingToNewFolder) {
      // Create new folder first
      if (maxFoldersReached.value) {
        handleFolderQuotaExceeded()
        isSavingToFolder.value = false
        return
      }
      const newFolder = await libraryStore.createFolder(newFolderName.value, userId.value)
      analytics.sendEvent('Added New Folder')
      analytics.sendEvent('Added New Student') // to be deprecated
      currentFolder.value = newFolder
    }

    if (!currentFolder.value?.uuid && tabValue.value !== TabType.NEW) {
      errorMessage.value = 'Please select a folder.'
      isSavingToFolder.value = false
      return
    }

    let createdContentUuid = props.contentUuid
    let savedContent: unknown

    if (props.contentType === CONTENT_TYPES.STRATEGY) {
      sendStrategySaveClicked()
    }

    if (isImpactOfDisability()) {
      const duplicateStatement = {
        ...props.contentPayload,
        user_uuid: userId.value,
        student_uuid: currentFolder.value?.uuid || '',
        student_name: currentFolder.value?.name || ''
      }

      const newStatement = await postImpactOfDisabilityStatement(duplicateStatement)
      createdContentUuid = newStatement.uuid
      savedContent = newStatement
      emit('statement-created', newStatement)
    } else {
      savedContent = await libraryStore.saveToLibrary({
        contentType: props.contentType,
        contentUuid: props.contentUuid,
        collectionUuid: currentFolder.value?.uuid || '',
        contributor: userId.value,
        folder: { uuid: currentFolder.value?.uuid || '', name: currentFolder.value?.name || '' },
        contentPayload: props.contentPayload,
        notes: sanitizedNotes(),
      })
    }

    sendAnalyticsEvent(isSavingToNewFolder)
    showDialog.value = false
    emit('save', { uuid: currentFolder.value?.uuid || '', name: currentFolder.value?.name || '' }, savedContent)
    emit('update:visible', false)

    if (props.postSaveNavigate) {
      navigateToSavedContent(createdContentUuid)
    }
  } catch (e) {
    console.error('Error saving to folder:', e)
    errorMessage.value = 'Save failed.'
    emit('saveerror', e)
  } finally {
    isSavingToFolder.value = false
  }
}

function existingFolderQuotaError() {
  if (tabValue.value === TabType.NEW || !currentFolder.value?.uuid) return null
  return folderQuotaErrorMessage(props.contentType, currentFolder.value.uuid)
}

function sanitizedNotes() {
  if (!props.enableNotes) return undefined
  return DOMPurify.sanitize(notes.value, { ALLOWED_TAGS: [], ALLOWED_ATTR: [] })
}

function navigateToSavedContent(contentUuid?: string) {
  switch (props.contentType) {
    case CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY:
      router.replace({
        name: 'PresentlevelsSavedSummary',
        params: {
          summary_uuid: contentUuid || props.contentUuid
        }
      })
      break
    case CONTENT_TYPES.SAVED_IMPACT_OF_DISABILITY:
      router.replace({
        name: 'ImpactOfDisabilitySavedStatement',
        params: {
          statement_uuid: contentUuid || props.contentUuid
        }
      })
      break
    default:
      console.warn(`Content-type '${props.contentType}' not supported for post-save navigation`)
  }
}

// Sent as the save goes out, like Vue 2, so a save the server refuses is still counted.
function sendStrategySaveClicked() {
  analytics.sendEvent(EVENT_NAMES.LIBRARY.SAVE_TO_LIBRARY.STRATEGY, {
    ...contentProperties.value,
    user_folder_role: libraryStore.getUserRoleForFolder(currentFolder.value?.uuid || ''),
  }, contentProperties.value)
}

function sendAnalyticsEvent(isNewStudent: boolean = false) {
  const userFolderRole = libraryStore.getUserRoleForFolder(currentFolder.value?.uuid || '')
  const isCopyAction = !!props.preSaveFunction

  switch (props.contentType) {
    case CONTENT_TYPES.GOAL:
    case CONTENT_TYPES.SUPER_PAGE:
      analytics.sendEvent(EVENT_NAMES.LIBRARY.SAVE_TO_LIBRARY.GOAL, {
        ...contentProperties.value,
        user_folder_role: userFolderRole,
      }, contentProperties.value)
      break
    case CONTENT_TYPES.PRESENTLEVEL:
    case CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY: {
      const eventName = isCopyAction
        ? EVENT_NAMES.LIBRARY.SAVE_TO_LIBRARY.COPIED_TO_ANOTHER_FOLDER
        : EVENT_NAMES.LIBRARY.SAVE_TO_LIBRARY.PRESENT_LEVELS
      analytics.sendEvent(eventName, {
        plw_uuid: props.contentUuid,
        plw_new_student: isNewStudent,
        user_folder_role: userFolderRole,
        ...deprecatedPresentLevelsSaveEventData(props.contentType, props.contentUuid)
      })
      break
    }
    case CONTENT_TYPES.RESOURCE:
      analytics.sendEvent(EVENT_NAMES.RESOURCES.SAVED, {
        ...contentProperties.value,
        student_uuid: currentFolder.value?.uuid,
        user_folder_role: userFolderRole,
      }, contentProperties.value)
      break
  }
}

function selectPreselectedFolder() {
  if (!folderToPreselect.value || !isOffered(folderToPreselect.value)) {
    return
  }
  if (props.contentType !== CONTENT_TYPES.PRESENTLEVEL_STUDENT_SUMMARY) {
    currentFolder.value = folderToPreselect.value
      // if a shared folder is pre-selected, switch to the shared tab
      const isShared = sharedFolders.value.some(f => f.uuid === folderToPreselect.value!.uuid)
      if (isShared) {
        tabValue.value = TabType.SHARED
      }
    return
  }
  const isOwned = libraryStore.userOwnedFolders.some(f => f.uuid === folderToPreselect.value!.uuid)
  if (isOwned) {
    currentFolder.value = folderToPreselect.value
  }
}

onMounted(async () => {
  try {
    await Promise.all([
      libraryStore.fetchRecentFolders(userId.value),
      libraryStore.fetchFolders(userId.value)
    ])
    selectPreselectedFolder()
  } catch (e) {
    console.error(e)
    loadFoldersError.value = true
  }
})

watch(showDialog, (isOpen) => {
  if (!isOpen) {
    // Reset all modal state when closed
    errorMessage.value = ''
    isSavingToFolder.value = false
    currentFolder.value = null
    newFolderName.value = ''
    notes.value = ''
    ownedFolderNameFilter.value = ''
    sharedFolderNameFilter.value = ''
    tabValue.value = TabType.EXISTING
    // Notify parent component that modal is closed
    emit('update:visible', false)
  }
})

const tabStyles = 'px-4 py-3 hover:bg-gray-300 text-blue-400 rounded-t-sm data-[state=active]:bg-blue-400 data-[state=active]:text-white font-medium focus:outline focus:outline-2 focus:outline-blue-400 flex items-center'

function sharedFolderAriaLabel(folder: LibraryFolder) {
  const owner = getFolderOwnerRole(folder)
  return `Folder name: ${folder.name}`
}
</script>
<template>
  <GbDialog :dialogId="dialogId" v-model:visible="showDialog" :header="customHeader || `Save ${props.contentTypeLabel} to Library`" :footer-buttons="true">
    <template #bodyContent>
      <TabsRoot @update:modelValue="handleTabValueChange" class="w-full" v-model="tabValue" :default-value="TabType.EXISTING">
        <TabsList class="flex mb-4 border-t-0 border-b-4 border-b-blue-400 gap-1">
          <TabsTrigger
            :value="TabType.EXISTING"
            :class="tabStyles"
          >
            My Folders
          </TabsTrigger>
          <TabsTrigger
            :value="TabType.SHARED"
            :class="tabStyles"
          >
            <span
              class="material-icons group-icon mr-1"
              aria-hidden="true">
              group
            </span>
             Shared With Me
          </TabsTrigger>
          <TabsTrigger
            :value="TabType.NEW"
            :class="tabStyles"
          >
            New Folder
          </TabsTrigger>
        </TabsList>

        <TabsContent :value="TabType.EXISTING">
          <div v-if="allFolders && allFolders.length > 0 && !loadingFolders && !loadFoldersError">
            <div class="mb-6" v-if="allFolders.length > 5">
              <Label
                for="folderFilter"
                class="text-lg font-medium mb-2 block text-gray-700 sr-only"
              >
                Filter folders
              </Label>
              <span class="p-input-icon-left w-full">
                <InputText
                  id="folderFilter"
                  v-model="ownedFolderNameFilter"
                  placeholder="Filter folders"
                  class="w-full"
                />
              </span>
            </div>

            <div v-if="filteredRecentFolders.length > 0" class="mb-4">
              <h3 class="text-lg font-medium mb-2">Recent folders</h3>
              <ul class="flex flex-wrap gap-2">
                <li v-for="folder in filteredRecentFolders" :key="folder.uuid" class="flex">
                  <GbButton
                    :label="folder.name"
                    size="small"
                    :gbTheme="isSelectedFolder(folder) ? 'bluePrimary' : 'blueTertiary'"
                    :aria-pressed="isSelectedFolder(folder)"
                    class="m-1 fs-exclude"
                    @click="selectFolder(folder)"
                  >
                    {{ folder.name }}
                  </GbButton>
                </li>
              </ul>
            </div>

            <div v-if="filteredOtherFolders.length > 0" class="mb-4">
              <h3 class="text-lg font-medium mb-2">Other folders</h3>
              <ul class="flex flex-wrap gap-2">
                <li v-for="folder in filteredOtherFolders" :key="folder.uuid" class="flex">
                  <GbButton
                    :label="folder.name"
                    size="small"
                    :gbTheme="isSelectedFolder(folder) ? 'bluePrimary' : 'blueTertiary'"
                    :aria-pressed="isSelectedFolder(folder)"
                    class="m-1 fs-exclude"
                    @click="selectFolder(folder)"
                  >
                    {{ folder.name }}
                  </GbButton>
                </li>
              </ul>
            </div>
          </div>
          <span v-else-if="loadingFolders">Loading folders...</span>
          <GbInlineMessage v-else-if="loadFoldersError" gbTheme="grayDark">
            <template #icon><span class="material-icons-round"> info_outline </span></template>
            We're not able to retrieve your existing folder names. Try again later.
          </GbInlineMessage>
        </TabsContent>

        <TabsContent :value="TabType.SHARED">
          <div v-if="sharedFolders && sharedFolders.length > 0 && !loadingFolders && !loadFoldersError">
            <div class="mb-6" v-if="sharedFolders.length > 5">
              <Label
                for="sharedFolderFilter"
                class="text-lg font-medium mb-2 block text-gray-700 sr-only"
              >
                Filter folders
              </Label>
              <span class="p-input-icon-left w-full">
                <InputText
                  id="sharedFolderFilter"
                  v-model="sharedFolderNameFilter"
                  placeholder="Filter folders"
                  class="w-full"
                />
              </span>
            </div>

            <div class="mb-2">
              <h3 class="text-lg font-medium mb-2">Folders shared with me</h3>
              <ul class="flex flex-wrap gap-2">
                <li v-for="folder in filteredSharedFolders" :key="folder.uuid" class="flex">
                  <TooltipProvider :delayDuration="200">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <GbButton
                          :label="folder.name"
                          size="small"
                          :gbTheme="isSelectedFolder(folder) ? 'bluePrimary' : 'blueTertiary'"
                          :aria-pressed="isSelectedFolder(folder)"
                          class="m-1 fs-exclude"
                          :aria-label="sharedFolderAriaLabel(folder)"
                          @click="selectFolder(folder)"
                        >
                          {{ folder.name }}
                          <span class="ml-2 mr-[-1]">
                            <div>
                              <FolderAvatar
                                :user-id="getFolderOwnerRole(folder)?.user_uuid || ''"
                                :folder="folder"
                                :right-margin="false"
                                :disable-tooltip="true"
                              />
                            </div>
                          </span>
                        </GbButton>
                      </TooltipTrigger>
                      <TooltipContent
                        class="fs-exclude max-w-[300px] tooltip-style-z"
                        :disablePortal="true"
                        side="bottom"
                        role="presentation">
                        <p>Owner: {{ getFolderOwnerRole(folder)?.user_name }}</p>
                        <p v-if="getFolderOwnerRole(folder)?.user_email">
                          {{ getFolderOwnerRole(folder)?.user_email }}
                        </p>
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </li>
              </ul>
              <GbInlineMessage
                :gbTheme="isPresentlevel() ? 'red' : 'primary'"
                class="flex w-full items-center gap-2 rounded text-gray-700 text-sm leading-tight mt-3 border  p-3"
                role="alert"
              >
                <template #icon>
                  <span class="material-icons-round text-red-300" aria-hidden="true">warning_amber</span>
                </template>
                <span class="font-bold">Edit access:</span>&nbsp;
                <span v-if="isPresentlevel()">You are not the owner of the folders in this 'Shared with Me' tab. If you contribute to this folder now, you will no longer be able to edit this statement. Only the owner will be able to edit. <span class="font-bold">Select Cancel to continue editing.</span></span>
                <span v-else>Once you contribute this to a Shared Folder, only the Owner of the folder will be able to make edits to it.</span>
              </GbInlineMessage>
            </div>

          </div>
          <span v-else-if="loadingFolders">Loading folders...</span>
          <GbInlineMessage v-else-if="loadFoldersError" gbTheme="grayDark">
            <template #icon><span class="material-icons-round"> info_outline </span></template>
            We're not able to retrieve your existing folder names. Try again later.
          </GbInlineMessage>
          <GbInlineMessage v-else gbTheme="grayDark">
            <template #icon><span class="material-icons-round"> info </span></template>
            No folders have been shared with you yet.
          </GbInlineMessage>
        </TabsContent>

        <TabsContent :value="TabType.NEW">
          <div class="mb-4">
            <NewFolderInput
              v-model="newFolderName"
              :isDuplicate="!isNewFolder && !isSavingToFolder"
              :maxReached="maxFoldersReached"
              @update:modelValue="newFolderNameUpdated"
              :isLoading="isSavingToFolder"
              @enter="saveToFolder"
            />
          </div>
        </TabsContent>
      </TabsRoot>

      <div v-if="enableNotes" class="mt-4 pt-4 border-t border-gray-300">
        <label for="saveToFolderNotes" class="block text-lg font-medium">
          <span class="block mb-2">Add Notes <span class="text-gray-700 font-regular">You can add/change this later</span></span>
          <textarea
            id="saveToFolderNotes"
            v-model="notes"
            class="w-full placeholder-gray-700 py-2 px-3 border border-gray-700 rounded-sm text-text-md font-regular fs-exclude"
            :placeholder="notesPlaceholder"
            rows="3"
          />
        </label>
      </div>
    </template>

    <template #footerButtonLeft>
      <span v-if="errorMessage" role="alert" class="mr-2 text-red-300 flex"><span class="material-icons-round mr-1" aria-hidden="true">warning_amber</span>{{ errorMessage }}</span>
      <GbButton
        type="button"
        gbTheme="grayOutlineSecondary"
        label="Cancel"
        @click="showDialog = false"
      />
    </template>

    <template #footerButtonRight>
      <GbButton
        :disabled="disableSaveButton"
        type="button"
        gbTheme="greenPrimary"
        :label="saveButtonLabel"
        @click="saveToFolder"
      >
        <span v-if="isSavingToFolder" class="flex items-center">
          <ProgressSpinner class="mr-2 !h-5 !w-5 stroke-white" strokeWidth="4" />
          Saving...
        </span>
        <span v-else class="flex items-center gap-1">
          <span v-if="tabValue === TabType.SHARED" class="material-icons-round">send</span>
          {{ saveButtonLabel }}
        </span>
      </GbButton>
    </template>
  </GbDialog>
</template>